import { type Config } from 'tailwindcss'

export const extendedTheme = {
	// * DESIGN SYSTEM: for all the sizes(width, height, padding, margin, spacing, gap) we use only these values
	//  1, 2, 3, 4, 6, 8, 12, 16, 24, 32, 48, 64, 96, 128, 160, 192
	width: {
		'128': '32rem', // 512px
		'160': '40rem', // 640px
		'192': '48rem', // 768px
	},
	height: {
		'128': '32rem', // 512px
		'160': '40rem', // 640px
		'192': '48rem', // 768px
	},
	padding: {
		'128': '32rem', // 512px
		'160': '40rem', // 640px
		'192': '48rem', // 768px
	},
	margin: {
		'128': '32rem', // 512px
		'160': '40rem', // 640px
		'192': '48rem', // 768px
	},
	colors: {
		border: 'hsl(var(--border))',
		input: {
			DEFAULT: 'hsl(var(--input))',
			invalid: 'hsl(var(--input-invalid))',
		},
		ring: {
			DEFAULT: 'hsl(var(--ring))',
			invalid: 'hsl(var(--foreground-destructive))',
		},
		background: 'hsl(var(--background))',
		foreground: {
			DEFAULT: 'hsl(var(--foreground))',
			destructive: 'hsl(var(--foreground-destructive))',
		},
		primary: {
			50: 'hsl(var(--primary-50))',
			100: 'hsl(var(--primary-100))',
			200: 'hsl(var(--primary-200))',
			300: 'hsl(var(--primary-300))',
			400: 'hsl(var(--primary-400))',
			500: 'hsl(var(--primary-500))',
			600: 'hsl(var(--primary-600))',
			700: 'hsl(var(--primary-700))',
			800: 'hsl(var(--primary-800))',
			900: 'hsl(var(--primary-900))',
			950: 'hsl(var(--primary-950))',
		},
		secondary: {
			50: 'hsl(var(--secondary-50))',
			100: 'hsl(var(--secondary-100))',
			200: 'hsl(var(--secondary-200))',
			300: 'hsl(var(--secondary-300))',
			400: 'hsl(var(--secondary-400))',
			500: 'hsl(var(--secondary-500))',
			600: 'hsl(var(--secondary-600))',
			700: 'hsl(var(--secondary-700))',
			800: 'hsl(var(--secondary-800))',
			900: 'hsl(var(--secondary-900))',
			950: 'hsl(var(--secondary-950))',
		},
		destructive: {
			DEFAULT: 'hsl(var(--destructive))',
			foreground: 'hsl(var(--destructive-foreground))',
		},
		muted: {
			DEFAULT: 'hsl(var(--muted))',
			foreground: 'hsl(var(--muted-foreground))',
		},
		accent: {
			DEFAULT: 'hsl(var(--accent))',
			foreground: 'hsl(var(--accent-foreground))',
		},
		popover: {
			DEFAULT: 'hsl(var(--popover))',
			foreground: 'hsl(var(--popover-foreground))',
		},
		card: {
			DEFAULT: 'hsl(var(--card))',
			foreground: 'hsl(var(--card-foreground))',
		},
	},
	borderColor: {
		DEFAULT: 'hsl(var(--border))',
	},
	borderRadius: {
		lg: 'calc(var(--radius) + 16px)',
		md: 'var(--radius)',
		sm: 'calc(var(--radius) - 8px)',
	},
	fontSize: {
		// 1rem = 16px

		// Headings
		h1: ['4.5rem', { lineHeight: '5.125rem', fontWeight: '700' }], // 72px size / 82px high / bold
		h2: ['3.75rem', { lineHeight: '4.25rem', fontWeight: '700' }], // 60px size / 68px high / bold
		h3: ['3rem', { lineHeight: '3.5rem', fontWeight: '700' }], // 48px size / 56px high / bold
		h4: ['2.25rem', { lineHeight: '2.75rem', fontWeight: '700' }], // 36px size / 44px high / bold
		h5: ['1.875rem', { lineHeight: '2.375rem', fontWeight: '700' }], // 30px size / 38px high / bold
		h6: ['1.5rem', { lineHeight: '2rem', fontWeight: '700' }], // 24px size / 32px high / bold

		// Body Text
		xl: ['1.25rem', { lineHeight: '1.75rem' }], // 20px size / 28px high / normal
		lg: ['1.125rem', { lineHeight: '1.625rem' }], // 18px size / 26px high / normal
		base: ['1rem', { lineHeight: '1.5rem' }], // 16px size / 24px high / normal
		sm: ['0.875rem', { lineHeight: '1.375rem' }], // 14px size / 22px high / normal
		xs: ['0.75rem', { lineHeight: '1.125rem' }], // 12px size / 18px high / normal

		/** 18px size / 24px high / semibold */
		caption: ['1.125rem', { lineHeight: '1.5rem', fontWeight: '600' }],
		/** 12px size / 16px high / bold */
		button: ['0.75rem', { lineHeight: '1rem', fontWeight: '700' }],
	},
	fontFamily: {
		sans: ['Montserrat', 'sans-serif'],
		heading: ['Montserrat', 'sans-serif'],
	},
	boxShadow: {
		'inset-sm': 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
		'inset-md': 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
		'inset-lg': 'inset 0 4px 6px rgba(0, 0, 0, 0.1)',
	},
} satisfies Config['theme']
